import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import { LocalizedService, useLocalizedServiceFactory } from '../../../API/GoMoto';
import { useCart } from '../../../context/cart';
import { useLocalization } from '../../../context/Localization/localization';
import { usePageState } from '../../../context/Page/page';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { minEstCost, rndMinEstCost } from '../../../helpers/minEstCost';
import css from './Header.module.scss';

import { Cart } from './Cart/Cart';
import { KioskHeader } from './KioskHeader/KioskHeader';
import { WebHeader } from './WebHeader/WebHeader';
import { CartItemType } from './Cart/Cart.types';
import type { CartItem } from './Cart/Cart.types';
import type { HeaderProps } from './Header.types';

export const Header = memo((props: HeaderProps) => {
    const { services, maintMenus, xtime, rndServices } = useCart();
    const [showCart, setShowCart] = useState(false);
    const [serviceItems, setServiceItems] = useState<CartItem[]>([]);
    const serviceFactory = useLocalizedServiceFactory();
    const { headerProps } = usePageState();
    const Localization = useLocalization();

    useEffect(() => {
        const serviceItems: CartItem[] = services
            .filter((s) => s.isTransport !== true)
            .map(serviceFactory)
            .map((service) => ({
                name: service.localization.name,
                price: (
                    <SpgPriceText
                        price={service.price}
                        priceLow={service.priceLow}
                        priceHigh={service.priceHigh}
                        spgPricing={service.spgPricing}
                        useSpgPricingLogic={!!service.useSpgPricing}
                    />
                ),
                minEstCost: minEstCost(service) ?? undefined,
                serviceId: service.id,
                type: CartItemType.Service,
            }));

        maintMenus?.forEach((m) => {
            serviceItems.push({
                name: m.title,
                price: <SpgPriceText
                    priceLow={m.priceLow}
                    priceHigh={m.priceHigh}
                    spgPricing={m.spgPricing}
                    showSingleWhenNoRange={false}
                />,
                minEstCost: minEstCost(m) ?? undefined,
                serviceId: m.id,
                scheduleOption: m.scheduleOption,
                type: CartItemType.MaintenanceMenu,
            });
        });

        xtime.services?.forEach((x) => {
            serviceItems.push({
                name: x.name,
                price: x.price,
                minEstCost: x.price,
                serviceId: x.id,
                xtimeType: x.type,
                type: CartItemType.XtimeService,
            });
        });

        xtime.menus?.forEach((x) => {
            serviceItems.push({
                name: x.name,
                price: x.totalPrice,
                minEstCost: x.totalPrice,
                serviceId: x.id,
                type: CartItemType.XtimeMenu,
            });
        });

        rndServices.forEach((rnd) => {
            if (!rnd.doneElsewhere) {
                const localizedService: LocalizedService | undefined = rnd.linkedService ? serviceFactory(rnd.linkedService) : undefined;

                serviceItems.push({
                    opCode: rnd.opCode,
                    name: localizedService && localizedService.localization.name ? localizedService.localization.name : rnd.description,
                    price: (
                        <SpgPriceText
                            price={rnd.price ?? localizedService?.price}
                            priceLow={localizedService?.priceLow}
                            priceHigh={localizedService?.priceHigh}
                            spgPricing={localizedService?.useSpgPricing ? localizedService?.spgPricing : undefined}
                        />
                    ),
                    minEstCost: rndMinEstCost(rnd) ?? undefined,
                    type: CartItemType.RNDService,
                });
            }
        });

        setServiceItems(serviceItems);
    }, [serviceFactory, services, maintMenus, xtime.services, xtime.menus, Localization, rndServices]);

    const renderHeader = () => (
        <>
            {isElectron() ? (
                <KioskHeader
                    onCartClick={() => setShowCart(!showCart)}
                    cartCount={serviceItems.length}
                    suppressLocalizationButtons={props.suppressLocalizationButtons}
                    suppressBanner={props.suppressBanner}
                    showSettingsButton={props.showSettingsButton}
                />
            ) : (
                <WebHeader onCartClick={() => setShowCart(!showCart)} cartCount={serviceItems.length} />
            )}
            <Cart
                items={serviceItems}
                visible={showCart}
                onClose={() => setShowCart(false)}
            />
        </>
    );

    const renderCustomHeader = () => (
        <nav data-testid={'custom-header-nav'} className={classNames(css.nav, css.customHeaderContent)}>
            <div>{headerProps?.leftIcons}</div>
            <div>{headerProps?.title}</div>
            <div>{headerProps?.rightIcons}</div>
        </nav>
    );

    return <header className={css.header}>{headerProps === null ? null : headerProps !== undefined ? renderCustomHeader() : renderHeader()}</header>;
});

Header.displayName = 'Header';
